import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { useApi } from '../../../design-system/useApi';
import { hrApi } from '../api';
import type { ShiftTemplate, ShiftAssignment } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  employeeId: string;
  employeeName: string;
  onSaved: () => void;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Assign an employee to a shift template (effective from a chosen date), or
 *  remove their currently-open assignment. Per-employee row action from the
 *  Schedule grid. */
export function AssignShiftDialog({ open, onOpenChange, employeeId, employeeName, onSaved }: Props) {
  const confirm = useConfirm();

  const templatesQ = useApi<{ templates: ShiftTemplate[] }>(
    open ? '/api/hr/schedule/templates?active_only=1' : null, [open],
  );
  const assignmentsQ = useApi<{ assignments: ShiftAssignment[] }>(
    open ? `/api/hr/schedule/assignments?employee_id=${employeeId}` : null, [open, employeeId],
  );

  const templates = templatesQ.data?.templates ?? [];
  const current = (assignmentsQ.data?.assignments ?? []).find((a) => a.effective_to == null);
  const currentTemplate = current ? templates.find((t) => t.id === current.template_id) : undefined;

  const [templateId, setTemplateId] = React.useState('');
  const [effectiveFrom, setEffectiveFrom] = React.useState(todayISO());
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) { setTemplateId(''); setEffectiveFrom(todayISO()); }
  }, [open]);

  async function assign(e: React.FormEvent) {
    e.preventDefault();
    if (!templateId) { toast.error('Pick a shift template'); return; }
    setBusy(true);
    try {
      await hrApi.createShiftAssignment({ employee_id: employeeId, template_id: templateId, effective_from: effectiveFrom });
      toast.success('Shift assigned');
      onSaved();
    } catch (err: any) {
      toast.error('Could not assign shift', { description: err.message });
    } finally { setBusy(false); }
  }

  async function removeCurrent() {
    if (!current) return;
    if (!(await confirm({
      title: 'Remove this assignment?',
      description: 'The employee falls back to their default hours (or an earlier assignment).',
      destructive: true,
      confirmLabel: 'Remove',
    }))) return;
    setBusy(true);
    try {
      await hrApi.deleteShiftAssignment(current.id);
      toast.success('Assignment removed');
      onSaved();
    } catch (err: any) {
      toast.error('Could not remove', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign shift — {employeeName}</DialogTitle>
        </DialogHeader>

        {current && (
          <div className="rounded-xl bg-muted/40 ring-1 ring-border/40 px-3.5 py-3 text-[12.5px] flex items-center justify-between gap-3">
            <span className="text-foreground">
              Currently on <span className="font-semibold">{currentTemplate?.name ?? 'a template'}</span> since {current.effective_from}
            </span>
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={removeCurrent} className="text-muted-foreground hover:text-destructive shrink-0">
              Remove
            </Button>
          </div>
        )}

        <form onSubmit={assign} className="grid gap-4 pt-1">
          <div className="grid gap-1.5">
            <Label>Shift template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger><SelectValue placeholder="Select a template" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {templates.length === 0 && (
              <p className="text-[12px] text-muted-foreground">No shift templates yet — create one first from Shift templates.</p>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label>Effective from</Label>
            <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || templates.length === 0}>{busy ? 'Saving…' : 'Assign'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
